import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';

import { useProjectFileApi } from '@/modules/project-file/hooks/use-project-file-api';
import { useDate } from '@/modules/utils/custom-hooks/use-date';
import { convertToCSV, downloadFile } from '@/modules/utils/download-file';
import { downloadBomApi } from '@/modules/project-file/api/project-file.api';

export const useProjectFilesTableParams = () => {
	const t = useTranslations();
	const { downloadProjectAttachment, isPending } = useProjectFileApi();
	const { format } = useDate();

	const fileExtensions = {
		image: { extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'], color: '#FF6347' },
		pdf: { extensions: ['pdf'], color: '#FF000F' },
		video: { extensions: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'], color: '#1E90FF' },
		word: { extensions: ['doc', 'docx'], color: 'blue' },
		excel: { extensions: ['xls', 'xlsx'], color: '#228B22' },
		text: { extensions: ['txt'], color: '#666666' },
		bom: { extensions: ['bom'], color: '#21A67A' },
	};

	const getIconNameAndColor = (name: string) => {
		const extension = name.split('.').pop()?.toUpperCase();
		const iconParams = { name: extension, color: '#666666' };

		if (name.includes('.pdf')) iconParams.color = fileExtensions.pdf.color;

		if (name.includes('T_BOM')) {
			iconParams.color = fileExtensions.bom.color;
			iconParams.name = 'BOM';
		}
		if (
			name.includes('.jpg') ||
			name.includes('.jpeg') ||
			name.includes('.png') ||
			name.includes('.gif') ||
			name.includes('.bmp') ||
			name.includes('.svg')
		)
			iconParams.color = fileExtensions.image.color;

		if (
			name.includes('.mp4') ||
			name.includes('.avi') ||
			name.includes('.mov') ||
			name.includes('.wmv') ||
			name.includes('.flv') ||
			name.includes('.mkv')
		)
			iconParams.color = fileExtensions.video.color;

		if (name.includes('.doc') || name.includes('.docx')) iconParams.color = fileExtensions.word.color;
		if (name.includes('.xls') || name.includes('.xlsx')) iconParams.color = fileExtensions.excel.color;

		return iconParams;
	};

	const downloadBom = async ({ file, type }: { file: Data.Entity<'api::project-file.project-file'>; type: 'xlsx' | 'pdc' }) => {
		const { data } = await downloadBomApi({ documentId: file.documentId, type });

		const byteCharacters = atob(data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
			const slice = byteCharacters.slice(offset, offset + 1024);
			const byteNumbers = new Array(slice.length);

			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			byteArrays.push(new Uint8Array(byteNumbers));
		}

		let blobFile: Blob;
		let fileName = `${t('T_BOM')}${file?.projectDocument?.name}`;

		if (type === 'xlsx') {
			blobFile = new Blob(byteArrays, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			fileName += '.xlsx';
			return downloadFile({ file: blobFile, fileName });
		}

		if (type === 'pdc') {
			const csvData = convertToCSV(byteArrays);
			blobFile = new Blob([csvData], { type: 'text/csv' });
			fileName += '.csv';

			return downloadFile({ file: blobFile, fileName });
		}
	};

	const downloadAttachment = async (projectFile: Data.Entity<'api::project-file.project-file'>) => {
		const { data } = await downloadProjectAttachment.mutateAsync({ documentId: projectFile.documentId });

		const response = await fetch(data.downloadUrl);
		const blobFile = await response.blob();
		let fileName = projectFile.title ?? '';
		let type: string | undefined;

		if (projectFile.isBom) {
			fileName = fileName.includes('T_BOM') ? fileName.replace('T_BOM', t('T_BOM')) : fileName;
			type = 'txt';
		}

		downloadFile({ file: blobFile, fileName, ...(type && { type }) });
	};

	return {
		t,
		downloadProjectAttachment,
		isPending,
		getIconNameAndColor,
		format,
		downloadBom,
		downloadAttachment,
	};
};
